import {attachAuthInterceptor} from '@/core/interceptors/authInterceptor'
import {attachErrorInterceptor} from '@/core/interceptors/errorInterceptor'
import {API_VERSION} from '@/utils/constants/commonConstants'
import {FeatureConfig} from '@/utils/constants/featuresFlag'
import {HTTP_METHODS} from '@/utils/constants/httpMethods'
import axios from 'axios'

// Function to create axios instance dynamically
const createApiClient = (baseUrlKey, shouldAttachAuthIntercepter = true) => {
  const baseURL = import.meta.env[FeatureConfig[baseUrlKey]] || import.meta.env[FeatureConfig.inventoryApiUrl]

  const client = axios.create({baseURL, timeout: 100000})

  // Attach interceptors
  if (shouldAttachAuthIntercepter) {
    attachAuthInterceptor(client)
  }
  attachErrorInterceptor(client)

  return client
}

// Function to get mock data
const getMockData = async config => {
  const method = config?.method || HTTP_METHODS.GET
  if (method === HTTP_METHODS.GET && config?.mockData) {
    return config?.mockData
  }
  if (method === HTTP_METHODS.GET && config?.mockPath) {
    const mockData = await import(config.mockPath)
    return mockData.default || {}
  }
  return {}
}

// Main request function
export const request = async (config, options = {}, queryParams = '', scope = 'pepsi') => {
  // Return mock data immediately if available
  if (config?.mockData) {
    return config.mockData
  }

  const method = config?.method || HTTP_METHODS.GET
  let endPoint = ''
  if (config?.growthbookSdkKey) {
    endPoint = `/${config.endPoint}/${import.meta.env[FeatureConfig[config?.growthbookSdkKey]]}`
  } else if (config?.baseUrlKey) {
    endPoint = `/${config.endPoint}`
  } else {
    if (config?.overrideApiVersionPrefix) {
      endPoint = config.endPoint
    } else {
      endPoint = `${config?.version || API_VERSION.V1}/${config.endPoint}`
    }
  }

  // Handle query params – append whenever they are provided
  // This ensures that even non-multipart POST requests can carry query parameters
  if (queryParams) {
    endPoint = `${endPoint}?${queryParams}`
  }

  const defaultOptions = {
    method,
    url: endPoint,
    headers: {'Content-Type': config?.contentType || 'application/json', ...config?.headers},
    ...options,
  }

  // Dynamically create client based on baseUrlKey or fallback to apiUrl
  const baseUrlKey = config?.baseUrlKey || 'inventoryApiUrl' // Default to 'apiUrl' if not provided
  const apiClient = createApiClient(baseUrlKey, config?.attachAuthIntercepter)
  if (options?.file) {
    const formData = new FormData()

    // append file on "file" key
    formData.append('file', options.file.file)

    // append any additional fields if provided
    if (options?.data && typeof options.data === 'object') {
      Object.entries(options.data).forEach(([key, value]) => {
        formData.append(key, value)
      })
    }

    defaultOptions.data = formData

    // Let browser set correct multipart boundary
    delete defaultOptions.headers['Content-Type']
  }

  // If formData is provided (for multipart requests), make adjustments
  if (options.formData instanceof FormData) {
    defaultOptions.data = options.formData

    defaultOptions.headers['Content-Type'] = 'multipart/form-data'
  }
  const hasQueryParams = endPoint?.includes('?')
  defaultOptions.url = endPoint + (hasQueryParams ? `&scope=pepsi` : `?scope=pepsi`)
  let response = {}
  try {
    response = await apiClient(defaultOptions)
  } catch (err) {
    console.error(err)
    const isTest = import.meta.env.MODE === 'test'
    if (isTest) {
      return await getMockData(config)
    }
    // Prefer propagating the raw Axios `response` object when available so that
    // calling code (component, hook, etc.) can inspect status codes and error
    // payloads without having to rely on axios-specific properties.
    // Fallback to the original error if no response object exists (e.g. network error).
    throw err?.response || err
  }
  return response?.data
}
